import PortalDialog, {PortalDialogProperties, PortalDialogState} from "../PortalDialog";
import React, {ReactNode} from "react";
import {Dialog} from "primereact/dialog";
import PortalDialogType from "../../PortalDialogType";
import {Button} from "primereact/button";
import Parameter from "../../parameter/Parameter";
import PortalDocument from "../../../document/PortalDocument";
import MimeTypeFilter from "../../../document/mimetype/MimeTypeFilter";
import MimeTypes from "../../../document/mimetype/MimeTypes";
import {WebServiceType, WebServiceTypes} from "@softvision/webpdf-wsclient-9-typescript";
import PortalDocumentManager from "../../../document/PortalDocumentManager";
import DialogDefault from "../../parameter/PortalDialogDefault";
import MimeType from "../../../document/mimetype/MimeType";
import Message from "../../../../shared/log/Message";
import {MessageType} from "../../../../shared/log/MessageInterface";
import {
	AdminWebserviceStatus,
	ClearSignature,
	Functions,
	Information,
	Security,
	Settings,
	SettingsInterface,
	Signature,
	SignatureEditProfile,
	SignaturesDocument
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import Translate from "../../../../shared/translation/Translate";
import Helper from "../../../../shared/components/Helper";
import Icon from "../../../../shared/components/Icon";
import FunctionHelper from "../../../../shared/helper/FunctionHelper";
import {InputSwitch} from "primereact/inputswitch";
import SignatureRemoveDialogFields from "../../field/signature/SignatureRemoveDialogFields";
import MessageBox from "../../../../shared/components/MessageBox";
import PortalDialogSecurity, {Intention} from "../../PortalDialogSecurity";

export class SignatureRemoveDialogDefinition extends PortalDialog<Signature, PortalDialogProperties<Signature>,
	PortalDialogState<Signature>> {
	/**
	 * Must be checked separately with this webservice. Otherwise, it would never be usable with AdobeRestrictions.
	 * Currently only usable with debug options.
	 */
	public static checkRestrictions(selectedDocuments: Array<PortalDocument>, signatureEditProfile: SignatureEditProfile) {
		for (let selectedDocument of selectedDocuments) {
			if (!PortalDialogSecurity.checkRestrictions(
				PortalDialogType.SIGNATURE_REMOVE, signatureEditProfile, Intention.editingContent, selectedDocument
			)) {
				return false;
			}

			if (FunctionHelper.applyValue(
				selectedDocument.metadata?.signatures?.signature, SignaturesDocument.getSignatureDefault()
			).length === 0) {
				return false;
			}
		}

		return selectedDocuments.length === 1;
	}

	public static isEnabled(portalInfo: Information): boolean {
		return FunctionHelper.applyValue(
			portalInfo.webserviceStatus?.webservices?.signature?.enabled, AdminWebserviceStatus.getEnabledDefault()
		) && FunctionHelper.applyValue(
			portalInfo.functionsStatus?.signDocument, Functions.getSignDocumentDefault()
		);
	}

	public static isActive(
		selectedDocuments: Array<PortalDocument>, portalInfo: Information, signatureEditProfile: SignatureEditProfile
	): boolean {
		let mimeTypes: Array<MimeType> = [];

		for (let selectedDocument of selectedDocuments) {
			mimeTypes.push(selectedDocument.getMimeType());

			if (selectedDocument.isLocked()) {
				return false;
			}

			if (!PortalDialogSecurity.checkRestrictions(
				PortalDialogType.SIGNATURE_REMOVE, signatureEditProfile, Intention.editingContent,
				selectedDocument
			)) {
				return false;
			}

			if (!selectedDocument.isEditable()) {
				return false;
			}

			if (FunctionHelper.applyValue(
				selectedDocument.metadata?.signatures?.signature, SignaturesDocument.getSignatureDefault()
			).length === 0) {
				return false;
			}
		}

		return this.isEnabled(portalInfo) && selectedDocuments.length === 1 &&
			MimeTypeFilter.checkSelection(mimeTypes, [MimeTypes.PDF]);
	}

	public renderContents(): ReactNode {
		let parameter: Signature | undefined = this.state.parameter?.data;

		if (!FunctionHelper.exists(parameter)) {
			return null;
		}

		return (
			<fieldset className="p-col-12 p-grid">
				<label className="p-col-1"/>
				<div className="p-col-11 switch-container">
					<InputSwitch inputId="control-flatten"
								 checked={FunctionHelper.applyValue(
									 parameter?.clear?.flatten, ClearSignature.getFlattenDefault()
								 )}
								 onChange={(e) => this.onChange(
									 SignatureRemoveDialogFields.FLATTEN, e.value
								 )}/>
					<label className="switch-label" htmlFor="control-flatten">
						<Translate {...this.props} data="signature_remove.flatten" namespace="dialog"/>
					</label>
				</div>
			</fieldset>
		)
	}

	public renderDialog(): ReactNode {
		let classList: Array<string> = ["signature-remove-dialog"];
		if (this.state.submitted) {
			classList.push("submitted");
		}

		return (
			<Dialog className={classList.join(" ")} visible={this.props.visible} onHide={this.hide}
					closeOnEscape={true} header={this.renderHeader()} footer={this.renderFooter()}
					resizable={false} appendTo={document.getElementById("root")}>
				<div className="p-grid">
					{this.renderError()}
					<Helper {...this.props}>
						<Translate {...this.props} data={"dialog.signature_remove.general"} namespace={"help"}
								   markdown={true}/>
					</Helper>
					<fieldset className="p-col-12 p-grid">
						<MessageBox {...this.props} type={MessageType.WARNING}>
							<Translate {...this.props} data={"dialog.signature_remove.info"} namespace={"help"}
									   markdown={true}/>
						</MessageBox>
					</fieldset>
					{this.renderContents()}
				</div>
			</Dialog>
		);
	}

	protected renderHeader(): ReactNode {
		return (
			<div>
				<h1>
					<Icon icon={"icon-delete small"}/>
					<Translate {...this.props} data="signature_remove.title" namespace="dialog"/>
				</h1>
				<Button icon="icon icon-close" onClick={() => this.hide()}/>
				{this.renderHelpButton()}
			</div>
		);
	}

	protected onChange(field: SignatureRemoveDialogFields, value: any): void {
		let parameter: Signature = this.state.parameter!.data;

		switch (field) {
			case SignatureRemoveDialogFields.FLATTEN:
				parameter.clear!.flatten = value;
				break;
		}

		this.setState({
			parameter: this.state.parameter
		});
	}

	protected isEditPasswordNeeded(portalDocument: PortalDocument): boolean {
		return super.isEditPasswordNeeded(portalDocument) && !FunctionHelper.applyValue(
			portalDocument.metadata?.security?.canModify, Security.getCanModifyDefault()
		);
	}

	protected async submit(): Promise<void> {
		await super.submit();

		let operationData: Signature = this.state.parameter!.data.clone();
		let webServiceType: WebServiceType = WebServiceTypes.SIGNATURE;
		let documentManager: PortalDocumentManager = this.props.context.documentManager;
		let selection: Array<PortalDocument> = documentManager.getSelection();

		for (let portalDocument of selection) {
			try {
				await this.props.context.session.executeRequest({
					webServiceType: webServiceType,
					document: portalDocument,
					operation: operationData,
					settings: Settings.fromJson({
						pdfa: {
							pdfaEditProfile: this.props.pdfaEditProfile
						},
						signature: {
							signatureEditProfile: this.props.signatureEditProfile
						},
						compress: {
							compressProfile: this.props.compressProfile,
							compressObjects: {
								objectStreamSize: this.props.objectStreamSize
							}
						}
					} as SettingsInterface)
				});
			} catch (exception: any) {
				if (await this.onError(exception)) {
					await this.submit();
				}
				return;
			}

			let message: Message = new Message("success.signature.remove", MessageType.SUCCESS);
			message.context = {filename: '"' + portalDocument.getFileName() + '" '};
			this.props.context.getPage().addMessage(message);
			this.hide();
		}
	}

	protected getDialogType(): PortalDialogType {
		return PortalDialogType.SIGNATURE_REMOVE;
	}

	protected getDefaultParameter(): Parameter<Signature> {
		return DialogDefault.getParameter(this.getDialogType(), this.props.context.documentManager.getSelection());
	}
}

const SignatureRemoveDialog = React.memo(SignatureRemoveDialogDefinition,
	(
		prevProps: Readonly<PortalDialogProperties<Signature>>, nextProps: Readonly<PortalDialogProperties<Signature>>
	) => {
		if (!nextProps.visible && !prevProps.visible) {
			return true
		}

		return false;
	}
);

export default SignatureRemoveDialog;